import { observer } from 'mobx-react-lite'
import { SkeletonListInfiniteScrollTrigger } from '@/components/SkeletonListInfiniteScrollTrigger'
import type { LegendVersion } from '@/features/legends/LegendVersion'
import {
  VersionPicker,
  VersionPickerItem,
  VersionPickerItemSkeleton,
} from '@/features/legends/versioning/components/VersionPicker'
import type { VersionManagerViewModel } from '@/features/legends/versioning/VersionManagerViewModel'
import { dateFormatters } from '@/utils/date-util'

const testId = 'legend-version-picker'

export interface VersionPickerContainerProps {
  vm: VersionManagerViewModel
}

export const VersionPickerContainer = observer(function VersionPickerContainer({
  vm,
}: VersionPickerContainerProps) {
  return (
    <VersionPicker testId={testId}>
      {vm.versions.map((version) => (
        <VersionPickerItemContainer
          key={version.version}
          vm={vm}
          version={version}
        />
      ))}
      <SkeletonListInfiniteScrollTrigger
        scroller={vm.versionScroller}
        maxSkeletons={4}
      >
        <VersionPickerItemSkeleton />
      </SkeletonListInfiniteScrollTrigger>
    </VersionPicker>
  )
})

interface VersionPickerItemContainerProps {
  vm: VersionManagerViewModel
  version: LegendVersion
}

const VersionPickerItemContainer = observer(
  function VersionPickerItemContainer({
    vm,
    version,
  }: VersionPickerItemContainerProps) {
    return (
      <VersionPickerItem
        version={version.version}
        status={vm.getVersionStatus(version)}
        notes={version.versionNotes}
        date={vm.getVersionDateLabel(version)}
        fullDate={dateFormatters.dateLongWithTime.format(
          vm.getVersionDate(version),
        )}
        updatedByName={version.lastUpdatedBy?.displayName ?? ''}
        isSelected={version === vm.selectedVersion}
        isPending={
          vm.selectVersion.pending && vm.selectVersion.args[0] === version
        }
        isDisabled={!vm.canSelectVersion}
        onPress={() => vm.selectVersion(version)}
        testId={`${testId}-version-${version.version}`}
      />
    )
  },
)
